import {
  HourglassIcon,
  LoaderIcon,
  TriangleAlertIcon,
  UserRoundIcon,
  type LucideIcon,
} from 'lucide-react';

import type { AttentionKind } from '@/features/tasks';
// The module, not the barrel: the barrel leads back here (tasks → BoardPage → …
// → workspace-overview), and this file is a leaf that others read at load.
import { READER_WAITING_LABEL } from '@/features/tasks/statusPresentation';

/**
 * Colour, for this page, is the board's colour.
 *
 * The three board states — `running`, `waiting`, `failed` — resolve to the same
 * three tokens here that a task card uses for them. A page that said "failed"
 * in a different red from the board it links to would undo in one glance the
 * agreement the derivation works to guarantee.
 *
 * Those are the state *keys*, not the words either surface prints: the board
 * writes "in progress", "waiting on you" (to a reader, "waiting on a member",
 * spec 193) and "errors", and `WorkspaceCounters` follows it. An earlier version of this comment claimed the page reused the
 * board's words while the counters printed the keys — the two had drifted, and
 * the comment was asserting an agreement that did not hold.
 *
 * Every value here is a semantic token defined in `globals.css`. Nothing on
 * this page picks a colour of its own; the tints below are `/N` opacity
 * modifiers over those same tokens, which is how the repository already
 * expresses hover and selection while the blue tint ramp is unavailable
 * (docs/brand.md OD-10).
 */

/**
 * The left rail on a row, matching the one a task card carries for the same
 * state.
 *
 * 3px, not the 1px this started at. A hairline of state colour against a white
 * card is legible only if you are already looking at the row, which defeats the
 * entire purpose: the rail exists so that *not* reading the page still tells
 * you something. Three pixels is where it registers peripherally and still
 * reads as an edge rather than as a coloured block — the brandbook's ceiling
 * for a secondary used as anything but a fill.
 */
export const ATTENTION_RAIL: Record<AttentionKind, string> = {
  'failed-run': 'bg-board-state-failed',
  'failed-workdir': 'bg-board-state-failed',
  orphaned: 'bg-board-state-failed',
  'human-gate': 'bg-board-state-waiting',
  'subtasks-to-start': 'bg-board-state-waiting',
  branching: 'bg-board-state-waiting',
  'spend-limit': 'bg-board-state-waiting',
  'needs-account': 'bg-board-state-waiting',
  // Muted, as the board draws a stopped run (`statusPresentation.stopped`): it is
  // listed because it holds its work, not because something broke.
  stopped: 'bg-muted-foreground',
};

/**
 * The faintest possible wash of the row's state across the card behind it.
 *
 * 4% — enough that a column of broken rows reads as a block of trouble at
 * arm's length, far too little to compete with the text on top of it or to
 * read as a filled surface. This is the difference between a page that looks
 * calm while five runs are broken and one that does not.
 *
 * Applied as an inert overlay element rather than as the card's own
 * background, so the wash composites over the card's white and the card keeps
 * `bg-card` for its hover to replace. A single `bg-*` class here would have
 * displaced `bg-card` outright and tinted the *page* through the card instead.
 */
export const ATTENTION_TINT: Record<AttentionKind, string> = {
  'failed-run': 'bg-board-state-failed/4',
  'failed-workdir': 'bg-board-state-failed/4',
  orphaned: 'bg-board-state-failed/4',
  'human-gate': 'bg-board-state-waiting/4',
  'subtasks-to-start': 'bg-board-state-waiting/4',
  branching: 'bg-board-state-waiting/4',
  'spend-limit': 'bg-board-state-waiting/4',
  'needs-account': 'bg-board-state-waiting/4',
  stopped: 'bg-muted-foreground/4',
};

/** Positions an `ATTENTION_TINT` wash across its row. */
export const TINT_OVERLAY = 'pointer-events-none absolute inset-0';

/**
 * The chip a row's kind is printed in.
 *
 * The kind was already a word; giving it the state's colour as well means the
 * row never carries its state in colour alone — the accessibility rule the
 * rail on its own cannot satisfy. Text sits on a 12% wash of the same token
 * rather than on the token itself, because these are the two secondaries the
 * brand approves as foregrounds (deep red 7.84:1, purple 5.43:1) and they stay
 * foregrounds here.
 */
export const ATTENTION_CHIP: Record<AttentionKind, string> = {
  'failed-run': 'bg-board-state-failed/12 text-board-state-failed',
  'failed-workdir': 'bg-board-state-failed/12 text-board-state-failed',
  orphaned: 'bg-board-state-failed/12 text-board-state-failed',
  'human-gate': 'bg-board-state-waiting/12 text-board-state-waiting',
  'subtasks-to-start': 'bg-board-state-waiting/12 text-board-state-waiting',
  branching: 'bg-board-state-waiting/12 text-board-state-waiting',
  'spend-limit': 'bg-board-state-waiting/12 text-board-state-waiting',
  'needs-account': 'bg-board-state-waiting/12 text-board-state-waiting',
  stopped: 'bg-muted text-muted-foreground',
};

/** The dot beside a group heading and a counter, in the same three colours. */
export const STATE_DOT = {
  running: 'bg-board-state-running',
  waiting: 'bg-board-state-waiting',
  failed: 'bg-board-state-failed',
} as const;

/**
 * The four states the overview reads work as (spec 150), with the one mark and
 * word each wears wherever it appears: the totals and the rows of every
 * section.
 *
 * Every state has a mark and a word, so none is told by colour alone (FR-012).
 * Waiting on subtasks and waiting on you share the waiting colour, as the board
 * draws them; the hourglass and the person are what set them apart. The
 * hourglass is the one the board card already shows on a parent ("Waiting on 3
 * of 4 · Design review"), so the overview and the board speak with one mark.
 */
export type OverviewState =
  'running' | 'waiting-on-subtasks' | 'waiting-on-you' | 'needs-attention';

export interface OverviewStateStyle {
  /** Decorative: the word beside it carries the meaning. */
  Icon: LucideIcon;
  word: string;
  /** Text colour for the mark and the figure. */
  text: string;
  /** Fill for a dot or a bar segment. */
  fill: string;
}

export const OVERVIEW_STATE: Record<OverviewState, OverviewStateStyle> = {
  running: {
    Icon: LoaderIcon,
    word: 'Running',
    text: 'text-board-state-running',
    fill: 'bg-board-state-running',
  },
  'waiting-on-subtasks': {
    Icon: HourglassIcon,
    word: 'Waiting on subtasks',
    text: 'text-board-state-waiting',
    fill: 'bg-board-state-waiting',
  },
  'waiting-on-you': {
    Icon: UserRoundIcon,
    word: 'Waiting on you',
    text: 'text-board-state-waiting',
    fill: 'bg-board-state-waiting',
  },
  'needs-attention': {
    Icon: TriangleAlertIcon,
    word: 'Needs attention',
    text: 'text-board-state-failed',
    fill: 'bg-board-state-failed',
  },
};

/**
 * A state's word for the person reading it. `canWork` is `useCanWork()`: a
 * reader cannot answer a gate, so for them it waits on a member (spec 193).
 */
export function overviewStateWord(
  state: OverviewState,
  canWork: boolean,
): string {
  if (!canWork && state === 'waiting-on-you') return READER_WAITING_LABEL;
  return OVERVIEW_STATE[state].word;
}

/**
 * A row inside a panel, rather than a card floating on the page.
 *
 * The rows used to be separate bordered cards with a gap between each. Five of
 * them cost five borders, five shadows' worth of visual weight and four gaps,
 * for information that is one list. Dividing rows inside a single panel says
 * "these belong together" and gives back about 40% of the section's height.
 *
 * `grid` rather than `flex`, so the dwell figure on the right can span both
 * text lines and stay vertically centred against them.
 *
 * The whole row is the click target, not the title inside it: a 14px line of
 * text is a poor thing to ask anyone to hit, and the rest of the row looks
 * clickable anyway. `STRETCHED_LINK` does that with `after:absolute`, which
 * keeps the metadata out of the link's accessible name.
 *
 * `motion-safe:` on the transition is now redundant rather than load-bearing:
 * spec 075 added the global reduced-motion rule this comment used to say did
 * not exist, and it collapses transition durations everywhere. Kept because it
 * costs nothing and states the intent locally; it is no longer the *only*
 * protection, which is what mattered.
 */
export const ROW =
  'border-border relative grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 border-t px-3 py-2 pl-4 transition-colors first:border-t-0 motion-safe:duration-base';

/**
 * What a row that can be opened does under a pointer and a keyboard.
 *
 * Separate from `ROW` so a row with a different column shape — the change feed
 * needs three, not two — can wear the same behaviour without inheriting a grid
 * that does not fit it. Appending an overriding `grid-cols-*` instead would
 * leave two of them in the class list and let source order in the stylesheet
 * decide, which is not a decision anyone can see in the markup.
 *
 * The focused state is `has-focus-visible`, not `focus-within`.
 *
 * `focus-within` fires whenever the stretched link takes focus — including on
 * a plain mouse click, which put a ring around every row the moment it was
 * clicked. `:focus-visible` is the selector that already knows the difference
 * between a pointer and a keyboard, and `:has()` is what lets the row wear its
 * own link's state: the link itself sets `focus-visible:outline-none` because
 * the ring belongs to the whole row, not to the 14px line of text inside it.
 */
export const ROW_INTERACTIVE_STATE =
  'hover:bg-accent has-focus-visible:ring-ring has-focus-visible:ring-inset has-focus-visible:ring-2';

export const ROW_INTERACTIVE = `${ROW} ${ROW_INTERACTIVE_STATE}`;

/** The panel rows sit in. Rounded once, at the group, not once per row. */
export const PANEL = 'border-border bg-card overflow-hidden rounded-lg border';

/**
 * Stretches an anchor across its row. Paired with `ROW_INTERACTIVE`.
 *
 * `after:inset-0` resolves against the nearest *positioned* ancestor, so the
 * element wrapping this link must not be `relative` — give it `z-10` instead.
 * The rows are grids, and a grid item takes `z-index` without `position`, so it
 * can sit above the tint wash without becoming the anchor's containing block.
 * When it was `relative`, the stretched area collapsed to the title line and
 * only the top of each row was clickable, while the hover still lit the whole
 * row — the pointer said one thing and the target did another.
 */
export const STRETCHED_LINK =
  'text-foreground truncate text-sm font-medium after:absolute after:inset-0 focus-visible:outline-none';

/**
 * The row's elapsed figure, at the right edge.
 *
 * It was buried mid-sentence in the metadata line — "Capture Smoke · Write
 * Files · 7d here · 2 attempts" — which is the wrong place for the number the
 * list is now sorted by. A sorted column's sort key belongs where the eye can
 * run down it, which is a right edge with `tabular-nums` so the digits align.
 */
export const ROW_DWELL =
  'col-start-2 row-start-1 row-span-2 self-center text-right text-xs leading-tight font-medium tabular-nums whitespace-nowrap';

/*
 * Every cell is placed explicitly rather than left to auto-flow.
 *
 * With `row-span-2` on the dwell figure, auto-placement puts the metadata line
 * in the second column and the figure underneath the title — the layout reads
 * as scrambled and the cause is invisible in the markup. Naming the cells costs
 * three classes and makes the row's shape a stated fact.
 */
export const ROW_TITLE =
  'col-start-1 row-start-1 flex min-w-0 items-center gap-2';
export const ROW_META = 'col-start-1 row-start-2 truncate text-xs';
/**
 * A metadata line that ends in the row's state (spec 151 FR-007).
 *
 * `ROW_META` truncates the whole line, so on a phone the state at its end was
 * the first thing cut, and the state is why the row is on the page. Here the
 * context sits in its own truncating span (`ROW_META_CONTEXT`) and the state
 * is a sibling that never shrinks; when both do not fit, the state wraps
 * onto its own line rather than being cut.
 */
export const ROW_META_SPLIT =
  'col-start-1 row-start-2 flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs';
export const ROW_META_CONTEXT = 'min-w-0 truncate';
/**
 * The state on a `ROW_META_SPLIT` line:
 * - `shrink-0`: never squeezed below its words by the context beside it;
 * - `max-w-full`: never wider than the line, so a long state ("Waiting on 3
 *   of 4 · Design review") wraps its own words on a phone instead of running
 *   over the column beside it.
 */
export const ROW_META_STATE = 'max-w-full shrink-0';
export const ROW_DETAIL = 'col-span-2 row-start-3 truncate font-mono text-2xs';

/**
 * A section's heading on this page.
 *
 * Deliberately not the same size as the row titles underneath it, which is what
 * it was: when a heading and its contents weigh the same, the page has no
 * skeleton and reads as one undifferentiated list. Small, spaced and upper-case
 * rather than large — the brandbook reserves all-caps for exactly this
 * (notations and eyebrows, lightly tracked), and a heading that competes with
 * its own content for attention is the thing being fixed.
 */
export { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';

/**
 * A group's count, as the first thing the eye lands on.
 *
 * Proportional figures, not `tabular-nums`: equal-width digits exist so columns
 * of numbers line up, and at display sizes they make a number like `121` read
 * loose and mechanical. Nothing on this page puts these in a column.
 */
export const GROUP_FIGURE = 'text-2xl leading-none font-light tracking-tight';

/**
 * A display figure: the number a panel or a tile is built around.
 *
 * Shared because two surfaces outside this page set one — the usage page's spend
 * and the console's tiles — so it now lives with the other shared pieces (spec 226
 * research R3) and is re-exported here for this page's callers.
 */
export { DISPLAY_FIGURE } from '@/shared/components/displayFigure';

/**
 * A display figure at rest: the count that is zero, the total that is nothing.
 *
 * Lighter in tone, so a calm figure recedes without disappearing. (It was lighter
 * in weight too, until every display figure became Light per FD-14 — spec 122
 * FR-009 — and weight stopped being a difference.)
 * Suppressing it entirely would make an organization where nothing is wrong look like
 * one whose figures failed to load, which is the distinction these surfaces exist to
 * keep (spec 074 FR-004).
 */
export const QUIET_FIGURE = 'text-muted-foreground/50';
